import React, { useEffect, useMemo, useState } from 'react';
import { reportsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Truck, Download, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { toast } from 'sonner';
import { exportToCSV } from '../../lib/exportCsv';
import { apiErrorMessage } from '../../lib/apiError';
import { ModelsSalesMap } from './ModelsSalesMap';
import { ModelCombobox } from '../ModelCombobox';

const fmtEuro = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

const GROUPS = [
    { key: 'modello', label: 'Modello' },
    { key: 'seller_name', label: 'Commerciale' },
    { key: 'country', label: 'Nazione' },
    { key: 'year', label: 'Anno' },
];

export const ModelsReport = () => {
    const { canViewTeam } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [year, setYear] = useState('all');
    const [seller, setSeller] = useState('all');
    const [country, setCountry] = useState('all');
    const [groupBy, setGroupBy] = useState('modello');
    const [splitBy, setSplitBy] = useState('none');
    const [modelFilter, setModelFilter] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Aggiornamento "in tempo reale": al ritorno sulla pagina, ogni 60s e su richiesta
    useEffect(() => {
        const bump = () => { if (document.visibilityState === 'visible') setRefreshKey(k => k + 1); };
        window.addEventListener('focus', bump);
        document.addEventListener('visibilitychange', bump);
        const t = setInterval(bump, 60000);
        return () => { window.removeEventListener('focus', bump); document.removeEventListener('visibilitychange', bump); clearInterval(t); };
    }, []);

    useEffect(() => {
        const load = async () => {
            if (data) setRefreshing(true); else setLoading(true);
            try {
                const { data } = await reportsAPI.models({
                    year: year !== 'all' ? year : undefined,
                    userId: seller !== 'all' ? seller : undefined,
                    country: country !== 'all' ? country : undefined,
                });
                setData(data);
                setUpdatedAt(new Date());
            } catch (e) {
                toast.error(apiErrorMessage(e, 'Errore nel caricamento statistica modelli'));
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, seller, country, refreshKey]);

    const rows = useMemo(() => {
        if (!data) return [];
        return modelFilter ? data.rows.filter(r => r.modello === modelFilter) : data.rows;
    }, [data, modelFilter]);
    const modelOptions = useMemo(() => [...new Set((data?.rows || []).map(r => r.modello))].sort(), [data]);
    const totals = useMemo(() => ({
        count: rows.reduce((a, r) => a + r.count, 0),
        value: rows.reduce((a, r) => a + r.value, 0),
    }), [rows]);

    const grouped = useMemo(() => {
        if (!data) return [];
        const m = {};
        rows.forEach(r => {
            const k1 = r[groupBy] ?? '—';
            const k2 = splitBy !== 'none' ? (r[splitBy] ?? '—') : null;
            const key = k2 !== null ? `${k1}||${k2}` : String(k1);
            if (!m[key]) m[key] = { group: k1, split: k2, count: 0, value: 0 };
            m[key].count += r.count;
            m[key].value += r.value;
        });
        return Object.values(m).sort((a, b) => b.count - a.count || String(a.group).localeCompare(String(b.group)));
    }, [data, rows, groupBy, splitBy]);

    const chartData = useMemo(() => {
        const m = {};
        grouped.forEach(g => { m[g.group] = (m[g.group] || 0) + g.count; });
        return Object.entries(m).map(([name, count]) => ({ name: String(name), count })).sort((a, b) => b.count - a.count).slice(0, 12);
    }, [grouped]);

    const groupLabel = GROUPS.find(g => g.key === groupBy)?.label || '';
    const splitLabel = GROUPS.find(g => g.key === splitBy)?.label || '';

    const [exporting, setExporting] = useState(false);
    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const res = await reportsAPI.downloadModelsExcel({
                year: year !== 'all' ? year : undefined,
                userId: seller !== 'all' ? seller : undefined,
                country: country !== 'all' ? country : undefined,
                modello: modelFilter || undefined,
            });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url; a.download = `statistica_modelli_venduti${year !== 'all' ? `_${year}` : ''}.xlsx`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success('Excel scaricato');
        } catch (e) {
            toast.error(apiErrorMessage(e, "Errore nell'export Excel"));
        } finally {
            setExporting(false);
        }
    };

    const handleExport = () => {
        const rows = (data?.rows || []).filter(r => !modelFilter || r.modello === modelFilter).map(r => ({
            Modello: r.modello,
            Commerciale: r.seller_name,
            Nazione: r.country,
            Anno: r.year,
            'Veicoli venduti': r.count,
            'Valore (EUR)': Math.round(r.value),
        }));
        exportToCSV('statistica_modelli_venduti', rows);
    };

    return (
        <Card data-testid="models-report">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                    <Truck size={16} /> Modelli Venduti
                </CardTitle>
                <div className="flex items-center gap-2 print:hidden">
                    {updatedAt && (
                        <span className="text-[11px] text-muted-foreground hidden sm:inline" data-testid="models-updated-at">
                            Aggiornato alle {updatedAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setRefreshKey(k => k + 1)} disabled={refreshing} title="Aggiorna adesso" data-testid="models-refresh">
                        <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={!rows.length || exporting} data-testid="models-report-export-excel" title="Excel con riepilogo e un foglio per nazione">
                        {exporting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <FileSpreadsheet size={14} className="mr-2" />} Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExport} disabled={!rows.length} data-testid="models-report-export">
                        <Download size={14} className="mr-2" /> CSV
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 print:hidden">
                    <div className="col-span-2 lg:col-span-1">
                        <Label className="text-xs uppercase tracking-wider">Modello</Label>
                        <ModelCombobox value={modelFilter} onChange={setModelFilter} placeholder="Tutti i modelli" testid="models-filter-model" options={modelOptions} />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Anno</Label>
                        <Select value={year} onValueChange={setYear}>
                            <SelectTrigger data-testid="models-filter-year"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli anni</SelectItem>
                                {(data?.years || []).map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {canViewTeam() && (
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Commerciale</Label>
                            <Select value={seller} onValueChange={setSeller}>
                                <SelectTrigger data-testid="models-filter-seller"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti</SelectItem>
                                    {(data?.sellers || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Nazione</Label>
                        <Select value={country} onValueChange={setCountry}>
                            <SelectTrigger data-testid="models-filter-country"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutte</SelectItem>
                                {(data?.countries || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Raggruppa per</Label>
                        <Select value={groupBy} onValueChange={(v) => { setGroupBy(v); if (splitBy === v) setSplitBy('none'); }}>
                            <SelectTrigger data-testid="models-group-by"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {GROUPS.map(g => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Dettaglio per</Label>
                        <Select value={splitBy} onValueChange={setSplitBy}>
                            <SelectTrigger data-testid="models-split-by"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Nessuno</SelectItem>
                                {GROUPS.filter(g => g.key !== groupBy).map(g => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-40"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                ) : !data || rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="models-report-empty">
                        Nessuna commessa con modello per i filtri selezionati.
                        {data?.without_model > 0 && ` (${data.without_model} commesse senza modello indicato)`}
                    </p>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-2 text-sm">
                            <Badge variant="outline" data-testid="models-total-count">{totals.count} veicoli venduti</Badge>
                            <Badge variant="outline">{fmtEuro(totals.value)}</Badge>
                            {data.without_model > 0 && (
                                <Badge variant="outline" className="text-amber-600 border-amber-300" data-testid="models-without">
                                    {data.without_model} senza modello
                                </Badge>
                            )}
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                            <div className="lg:col-span-2">
                                <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 32)}>
                                    <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 32 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                        <XAxis type="number" allowDecimals={false} />
                                        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" name="Veicoli" fill="#D32F2F" radius={[0, 4, 4, 0]}>
                                            <LabelList dataKey="count" position="right" style={{ fontSize: 11 }} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="lg:col-span-3 overflow-x-auto max-h-[460px] overflow-y-auto">
                                <table className="w-full text-sm data-table" data-testid="models-report-table">
                                    <thead className="sticky top-0 bg-white">
                                        <tr className="border-b bg-muted/30">
                                            <th className="text-left py-3 px-3">{groupLabel}</th>
                                            {splitBy !== 'none' && <th className="text-left py-3 px-3">{splitLabel}</th>}
                                            <th className="text-right py-3 px-3">Veicoli</th>
                                            <th className="text-right py-3 px-3">Valore</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {grouped.map((g, i) => (
                                            <tr key={i} className="border-b hover:bg-muted/20">
                                                <td className="py-2 px-3 font-medium">{String(g.group)}</td>
                                                {splitBy !== 'none' && <td className="py-2 px-3 text-muted-foreground">{String(g.split)}</td>}
                                                <td className="py-2 px-3 text-right font-semibold">{g.count}</td>
                                                <td className="py-2 px-3 text-right text-emerald-600">{fmtEuro(g.value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <ModelsSalesMap rows={rows} />
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default ModelsReport;
